/**
 * User Navigation Component
 *
 * Optimised to use UserContext from microservices data.
 * Requires UserProvider in parent component tree.
 */

"use client"

import { accountNavItems } from "@/components/account/accountNavData"
import { Badge } from "@/components/ui/badge"
import { Button, buttonVariants } from "@/components/ui/Button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Separator } from "@/components/ui/separator"
import UserAvatar from "@/components/ui/UserAvatar"
import { useUser } from "@/contexts/UserContext"
import { useApprovedRoles } from "@/hooks/useApprovedRoles"
import { useLogout } from "@/hooks/useLogout"
import { cn } from "@/lib/utils"
import {
  CheckCircle2,
  Download,
  LogIn,
  Shield,
  SquarePenIcon,
} from "lucide-react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import * as React from "react"
import WrongCompanyDialog from "../dialogs/WrongCompanyDialog"

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed"
    platform: string
  }>
  prompt(): Promise<void>
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean
}

export default function UserNav() {
  // Get user data from UserContext (microservices)
  const user = useUser()
  const router = useRouter()

  const displayName = user.name || ""
  const email = user.email || ""
  const photoUrl = user.pictureUrl || ""

  // Company info
  const companyName = user.companyName || ""
  const companyCode = user.companyCode || ""
  const companyOther = user.companyOther || ""

  // Membership info
  const membership = user.membership || ""

  const { logout } = useLogout()
  const [mounted, setMounted] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [deferredPrompt, setDeferredPrompt] =
    React.useState<BeforeInstallPromptEvent | null>(null)
  const [isStandalone, setIsStandalone] = React.useState(false)
  const [isWrongCompanyDialogOpen, setIsWrongCompanyDialogOpen] =
    React.useState(false)
  const [approvedRoles, setApprovedRoles] = React.useState<string[]>([])
  const [rolesLoading, setRolesLoading] = React.useState(true)

  // Si l'utilisateur n'est pas connecté (pas d'email), afficher un bouton Login
  const isAuthenticated = !!email

  // Fonction pour formater le nom du rôle (editor_restrail -> Editor Restrail)
  const formatRoleName = React.useCallback((role: string) => {
    return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  }, [])

  // Charger les rôles approuvés si l'utilisateur est authentifié
  // Utiliser le hook centralisé pour éviter les appels multiples
  const { approvedRoles: fetchedRoles, loading: rolesLoadingState } =
    useApprovedRoles({
      enabled: isAuthenticated,
    })

  React.useEffect(() => {
    if (!isAuthenticated) {
      setApprovedRoles([])
      setRolesLoading(false)
      return
    }

    // Filtrer le rôle "authenticated" qui n'est pas pertinent à afficher
    const roles = fetchedRoles.filter(
      (role: string) => role !== "authenticated"
    )
    setApprovedRoles(roles)
    setRolesLoading(rolesLoadingState)
  }, [isAuthenticated, fetchedRoles, rolesLoadingState])

  // Helper function to add MY_UIC_BASE_URL prefix to /account/* links
  const getAccountUrl = React.useCallback((url: string): string => {
    if (url.startsWith("/account/")) {
      const myUicBaseUrl = process.env.NEXT_PUBLIC_ACCOUNT_APP_URL || ""
      if (myUicBaseUrl && myUicBaseUrl.trim() !== "") {
        // Remove trailing slash from base URL if present
        const baseUrl = myUicBaseUrl.replace(/\/$/, "")
        return `${baseUrl}${url}`
      }
    }
    return url
  }, [])

  React.useEffect(() => setMounted(true), [])

  // PWA install logic
  React.useEffect(() => {
    if (!mounted) return

    // Check if app is already installed
    const checkStandalone = () => {
      setIsStandalone(
        window.matchMedia("(display-mode: standalone)").matches ||
          (navigator as NavigatorWithStandalone).standalone ||
          document.referrer.includes("android-app://")
      )
    }

    checkStandalone()

    // Handle install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt)

    // Handle app installed
    window.addEventListener("appinstalled", () => {
      setDeferredPrompt(null)
    })

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      )
    }
  }, [mounted])

  if (!mounted) return null

  // Si non authentifié, afficher un bouton Login
  // Lance directement le login Azure B2C (pas de SSO silencieux)
  if (!isAuthenticated) {
    const handleLogin = async () => {
      try {
        // Utiliser la page actuelle comme returnUrl
        const currentPath =
          typeof window !== "undefined" ? window.location.pathname : "/"

        const response = await fetch("/api/auth/signin", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            returnUrl: currentPath,
            silentSSO: false, // Login direct (pas de SSO silencieux)
          }),
        })

        const data = await response.json()

        if (data.authUrl) {
          // Redirect vers Azure B2C pour login
          window.location.href = data.authUrl
        } else if (data.authenticated) {
          // Déjà authentifié
          window.location.href = currentPath
        }
      } catch (error) {
        console.error("[UserNav] Login error:", error)
        // Fallback: redirect vers signin
        router.push("/signin")
      }
    }

    return (
      <Button
        variant="default"
        size="sm"
        onClick={handleLogin}
        className="ml-2"
        aria-label="Login"
      >
        <LogIn className="h-4 w-4 mr-2" />
        Login
      </Button>
    )
  }

  const handleLogout = () => {
    setOpen(false)
    logout()
  }

  const handleInstallClick = async () => {
    if (!deferredPrompt) return

    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === "accepted") {
      console.log("User accepted the install prompt")
    }

    setDeferredPrompt(null)
    setOpen(false)
  }

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button 
            variant="ghost" 
            className="rounded-full ml-2 h-auto p-2"
            aria-label={`User menu for ${displayName || email}`}
          >
            <UserAvatar userName={displayName} photoUrl={photoUrl} size="sm" />
            <div className="flex-1 text-left text-sm leading-tight hidden md:grid">
              <span className="truncate font-medium max-w-[190px]">
                {displayName}
              </span>
              {email ? (
                <span className="text-muted-foreground truncate max-w-[150px] text-xs">
                  {companyName}
                </span>
              ) : null}
            </div>
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md p-0">
          <DialogHeader>
            <DialogTitle>
              <div className="flex gap-3 items-start px-4 pt-3 pb-0">
                <UserAvatar
                  userName={displayName}
                  photoUrl={photoUrl}
                  size="lg"
                />
                <div className="grid flex-1 items-start leading-tight">
                  <span className="truncate font-semibold">{displayName}</span>
                  {email ? (
                    <span className="text-muted-foreground truncate text-sm font-normal lowercase">
                      {email}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="flex gap-3 items-start px-2 pt-3 pb-0">
                <Link
                  href={getAccountUrl("/account/edit")}
                  className={cn(
                    buttonVariants({ variant: "ghost" }),
                    "justify-start w-full"
                  )}
                  onClick={() => setOpen(false)}
                >
                  <SquarePenIcon className="h-3 w-3 mr-1" />
                  Edit my profile
                </Link>
              </div>
            </DialogTitle>
          </DialogHeader>
          <Separator />

          {/* Company Info - from microservices */}
          {(companyName || membership) && (
            <>
              <div className="px-6 space-y-2">
                {companyName && companyCode && (
                  <div>
                    <p className="text-sm font-medium">{companyName}</p>
                  </div>
                )}
                {!companyCode && companyOther && (
                  <p className="text-xs text-muted-foreground">
                    {companyOther}
                  </p>
                )}
                {membership && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">
                      {membership}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Approved Roles */}
          {isAuthenticated && (
            <>
              <Separator />
              <div className="px-6 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <p className="text-xs font-medium text-muted-foreground">
                    Approved Roles
                  </p>
                </div>
                {rolesLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-20 bg-muted animate-pulse rounded" />
                  </div>
                ) : approvedRoles.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {approvedRoles.map((role) => (
                      <Badge
                        key={role}
                        variant="outline"
                        className="text-xs px-2 py-0.5 border-green-400 "
                      >
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        {formatRoleName(role)}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      No approved roles yet
                    </p>
                    <Link
                      href="/request/access"
                      className="text-xs text-primary hover:underline"
                      onClick={() => setOpen(false)}
                    >
                      Request access →
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}

          <Separator />
          <div className="px-2">
            {accountNavItems.map((Item) => {
              const Icon = Item.icon
              return (
                <Button
                  key={Item.url}
                  variant="ghost"
                  className="w-full justify-start"
                  asChild
                  onClick={() => setOpen(false)}
                >
                  <Link
                    href={getAccountUrl(Item.url)}
                    className="flex items-center gap-3"
                  >
                    <Icon className="h-5 w-5" />
                    <span>{Item.title}</span>
                  </Link>
                </Button>
              )
            })}
          </div>

          {/* Install App Button - only show if not standalone and prompt available */}
          {!isStandalone && (
            <>
              <Separator />
              <Button
                variant="ghost"
                className="justify-start mx-2"
                onClick={handleInstallClick}
                aria-label={`Install ${process.env.NEXT_PUBLIC_APP_NAME || 'app'}`}
              >
                <Download className="mr-1" />
                <span>Install {process.env.NEXT_PUBLIC_APP_NAME}</span>
              </Button>
              <Separator />
            </>
          )}
        </DialogContent>
      </Dialog>
      <WrongCompanyDialog
        open={isWrongCompanyDialogOpen}
        onOpenChange={setIsWrongCompanyDialogOpen}
      />
    </div>
  )
}
